<template>
  <b-form-group
    :label="secondaryAppLabel"
    label-size="lg"
  >
    <SelectSecondaryApps :tab="tab" />

    <div
      v-if="showNotPublishedAlert"
      class="alert alert-warning"
      role="alert"
      data-test="alert-not-published-secondary-app"
    >
      <font-awesome-icon icon="exclamation-triangle" /> This App has not been published
    </div>

    <div
      v-if="showAppIsInternalAlert"
      class="alert alert-warning"
      role="alert"
      data-test="alert-internal-secondary-app"
    >
      <font-awesome-icon icon="exclamation-triangle" /> This app is INTERNAL and the consolidated app is LIVE, the INTERNAL app will not be displayed when published
    </div>

    <b-form-group
      label="Colour:"
      label-cols-lg="3"
      label-align-lg="right"
      class="mb-0"
    >
      <ColorPicker
        v-model="secondaryAppColour"
        :debounce="500"
        data-test="tab-colour-picker"
      />
    </b-form-group>

    <b-form-group
      label="Visible:"
      label-cols-lg="3"
      label-align-lg="right"
      class="mb-0 pt-1"
    >
      <div class="h-100 d-flex align-items-center">
        <b-checkbox
          v-model="secondaryAppVisible"
          switch
          data-test="tab-visible-input"
        />
      </div>
    </b-form-group>

    <b-form-group class="mb-0 pt-1">
      <b-button
        variant="danger"
        block
        data-test="remove-secondary-app"
        @click="removeSecondaryApp"
      >
        Remove App
      </b-button>
    </b-form-group>

    <b-form-group
      v-if="secondaryAppId"
      class="mb-0 pt-1"
    >
      <!-- Check if they have permissions ? -->
      <b-button
        block
        data-test="go-to-secondary-app"
        @click="goToSecondaryApp"
      >
        Go to {{ secondaryAppLabel }}
      </b-button>
    </b-form-group>
  </b-form-group>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import { ColorPicker } from '@orbica/vue-modules'
import SelectSecondaryApps from '@/components/builder/TabSettings/SelectSecondaryApps.vue'
import {
  AppStatuses,
  Tab,
  SecondaryAppReferenceNodeConfig,
  NodeConfigKeys,
} from '@/types'
import { NODE_CONFIG_KEY, goToStudio } from '@/constants'

const props = {
  tab: {
    type: Object as PropType<Tab>,
    required: true,
  },
}

const components = {
  ColorPicker,
  SelectSecondaryApps,
}

@Component({ components })
export default class SecondaryAppSettings extends Vue.extend({ props }) {
  get primaryApp () {
    return this.$typedStore.primary.app.app
  }

  get secondaryApps () {
    return this.$typedStore.primary.app.allOrganisationSecondaryApps
  }

  get secondaryAppReferenceNodes () {
    return this.$typedStore.primary.entities.nodes.secondaryAppReferenceNodes
  }

  get secondaryAppReferenceNode () {
    return this.secondaryAppReferenceNodes.find(nodes => nodes.path === this.tab.path)
  }

  get secondaryAppId () {
    return this.secondaryAppReferenceNode?.config.secondaryAppId ?? null
  }

  get secondaryAppColour () {
    return this.secondaryAppReferenceNode?.config.colour ?? null
  }

  set secondaryAppColour (colour) {
    this.updateNodeProp(colour, NodeConfigKeys.COLOUR)
  }

  get secondaryAppVisible () {
    return this.$typedStore.primary.entities.nodes.visible(this.tab.path) ?? true
  }

  set secondaryAppVisible (visible) {
    this.updateNodeProp(visible, NodeConfigKeys.VISIBLE)
  }

  get secondaryApp () {
    return this.secondaryApps.find(app => app.id === this.secondaryAppId)
  }

  get secondaryAppLabel () {
    if (this.secondaryApp) {
      return this.$typedStore.primary.app.secondaryAppLabel(this.secondaryApp)
    } else {
      return this.tab.name
    }
  }

  get showNotPublishedAlert () {
    if (this.secondaryApp) {
      return this.secondaryApp.status !== AppStatuses.LIVE && this.secondaryApp.status !== AppStatuses.INTERNAL
    }
    return false
  }

  get showAppIsInternalAlert () {
    if (this.secondaryApp) {
      return this.primaryApp.status === AppStatuses.LIVE && this.secondaryApp.status === AppStatuses.INTERNAL
    }
    return false
  }

  updateNodeProp (value: string | null | boolean, ...args: [keyof SecondaryAppReferenceNodeConfig] | []) {
    this.$typedStore.primary.entities.nodes.updateNodeProp({
      path: this.tab.path,
      configPath: [NODE_CONFIG_KEY, ...args],
      value,
    })
  }

  removeSecondaryApp () {
    this.$typedStore.primary.entities.nodes.removeSecondaryAppReferenceNode(this.tab.path)
  }

  goToSecondaryApp () {
    window.open(goToStudio(this.secondaryAppId!), '_self')
  }
}

</script>
